import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import Cta from "@/components/Cta";
import { Icon } from "@/components/Icon";
import { COMPANY } from "@/lib/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Why Choose Us", hash: "why-choose-us" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToWhy = () => {
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById("why-choose-us")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: "why-choose-us" } });
      setTimeout(
        () =>
          document
            .getElementById("why-choose-us")
            ?.scrollIntoView({ behavior: "smooth" }),
        350
      );
    }
  };

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-white border-b transition-colors duration-200 ${
        scrolled ? "border-[#2A2E7A]/12 shadow-sm" : "border-[#2A2E7A]/8"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between gap-6">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV.map((item) =>
              item.hash ? (
                <button
                  key={item.label}
                  onClick={goToWhy}
                  data-testid="nav-why-choose-us"
                  className="text-sm font-medium text-[#2A2E7A] hover:text-[#4B5CAB] transition-colors duration-200"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.to)
                      ? "text-[#4B5CAB]"
                      : "text-[#2A2E7A] hover:text-[#4B5CAB]"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={COMPANY.phoneHref}
              data-testid="header-phone-link"
              className="flex items-center gap-2 text-sm font-semibold text-[#2A2E7A] hover:text-[#4B5CAB] transition-colors duration-200"
            >
              <Icon name="Phone" className="h-4 w-4" />
              {COMPANY.phone}
            </a>
            <Cta to="/contact" variant="primary" className="px-5 py-2.5 text-sm" testId="header-request-quote">
              Request a Quote
            </Cta>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-md text-[#2A2E7A] hover:bg-[#F5F6FB] transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={open ? "X" : "Menu"} className="h-6 w-6" />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#2A2E7A]/8 bg-white" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) =>
              item.hash ? (
                <button
                  key={item.label}
                  onClick={goToWhy}
                  className="text-left px-2 py-3 text-base font-medium text-[#2A2E7A] hover:bg-[#F5F6FB] rounded-md transition-colors"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="px-2 py-3 text-base font-medium text-[#2A2E7A] hover:bg-[#F5F6FB] rounded-md transition-colors"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href={COMPANY.phoneHref}
              className="flex items-center gap-2 px-2 py-3 text-base font-semibold text-[#2A2E7A]"
            >
              <Icon name="Phone" className="h-4 w-4" />
              {COMPANY.phone}
            </a>
            <Cta to="/contact" variant="primary" className="mt-2 w-full" testId="mobile-request-quote">
              Request a Quote
            </Cta>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
